import { v4 as uuid } from 'uuid';
import type { VistoryRoom } from '../types/vistory.types';
import type { PropertyNestedResponse } from '@services/housien/types-property.d';

interface RoomDeriveInput {
  category?: string;
  bedrooms?: number;
  suites?: number;
  bathrooms?: number;
  parking_spots?: number;
}

// Default Brazilian-Portuguese room labels used when auto-deriving a vistoria
// layout. Named constants keep them out of the inline object-literal path and
// centralize the vocabulary for future localization.
const ROOM_LIVING = 'Sala';
const ROOM_KITCHEN = 'Cozinha';
const ROOM_BALCONY = 'Varanda';
const ROOM_BEDROOM = 'Quarto';
const ROOM_SUITE = 'Suíte';
const ROOM_BATHROOM = 'Banheiro';
const ROOM_SERVICE = 'Área de Serviço';
const ROOM_GARAGE = 'Garagem';

/**
 * Extracts the room-deriving inputs (category + counts) from a property.
 */
function extractInput(property: PropertyNestedResponse): RoomDeriveInput {
  return {
    category: property.classification?.category,
    bedrooms: property.characteristics?.bedrooms,
    suites: property.characteristics?.suites,
    bathrooms: property.characteristics?.bathrooms,
    parking_spots: property.characteristics?.parking_spots,
  };
}

/**
 * Expands a label into `count` names, numbering them when more than one.
 */
function repeat(label: string, count: number): string[] {
  if (count <= 1) return [label];
  return Array.from({ length: count }, (_, i) => `${label} ${i + 1}`);
}

/**
 * Builds the ordered list of room names for a vistoria from the property inputs.
 */
function roomNames(input: RoomDeriveInput): string[] {
  const suites = input.suites ?? 0;
  const bedrooms = Math.max((input.bedrooms ?? 0) - suites, 0);
  const parking = input.parking_spots ?? 0;
  const isCommercial = (input.category ?? '').toLowerCase().includes('comercial');

  const specs: Array<[string, number]> = [
    [ROOM_LIVING, 1],
    [ROOM_KITCHEN, 1],
    [ROOM_BEDROOM, bedrooms],
    [ROOM_SUITE, suites],
    [ROOM_BATHROOM, input.bathrooms ?? 0],
    [ROOM_SERVICE, 1],
    [ROOM_GARAGE, parking],
    [ROOM_BALCONY, isCommercial ? 0 : 1],
  ];

  return specs.flatMap(([label, count]) => (count > 0 ? repeat(label, count) : []));
}

/**
 * Derives an ordered list of rooms for a vistoria from a property's
 * characteristics. Uses category + bedroom/suite/bathroom/parking counts
 * to generate named rooms with stable IDs.
 */
export function deriveRoomsFromProperty(
  property: PropertyNestedResponse,
): VistoryRoom[] {
  const names = roomNames(extractInput(property));
  return names.map((name, index) => ({
    id: uuid(),
    name,
    order: index + 1,
    items: [],
    photos: [],
  }));
}
